import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import userService from '../../services/userService';

/**
 * useAdminUserSelection Hook
 *
 * Manages user selection for admin bookings.
 * Handles user loading, selection, contact prefill, and bypass advance hours.
 * Search/filtering is handled client-side by react-select component.
 *
 * State Ownership:
 * - Owns: users list, usersLoading
 * - Owns: selectedUserId, bypassAdvanceHours, contactPrefilled
 *
 * Integration Points:
 * - Reads: entityId, isAdminBooking from parent
 * - Updates: booking.contactNumber via callback
 *
 * @param {Object} params
 * @param {number} params.entityId - Current entity ID
 * @param {boolean} params.isAdminBooking - Whether in admin booking mode
 * @param {Function} params.onContactPrefill - Callback to update contact number
 * @param {Function} params.t - Translation function
 * @returns {Object} User selection state and handlers
 */
const useAdminUserSelection = ({ entityId, isAdminBooking, onContactPrefill, t }) => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [bypassAdvanceHours, setBypassAdvanceHours] = useState(false);
  const [contactPrefilled, setContactPrefilled] = useState(false);

  /**
   * Load all users from entity
   * Search/filtering is handled client-side by react-select
   */
  const loadEntityUsers = useCallback(async () => {
    if (!entityId) {
      return;
    }

    try {
      setUsersLoading(true);
      // Load a larger set of users since filtering is client-side
      const params = { limit: 100 };
      const response = await userService.getEntityUsers(entityId, params);
      const userList = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];
      setUsers(userList);
    } catch (error) {
      console.error('Failed to load entity users:', error);
      toast.error(t('errorLoadingUsers'));
    } finally {
      setUsersLoading(false);
    }
  }, [entityId, t]);

  /**
   * Reset all admin selection state when leaving admin mode
   */
  useEffect(() => {
    if (!isAdminBooking) {
      setSelectedUserId(null);
      setUsers([]);
      setBypassAdvanceHours(false);
      setContactPrefilled(false);
    }
  }, [isAdminBooking]);

  /**
   * Load users when admin booking mode is enabled
   */
  useEffect(() => {
    if (isAdminBooking && entityId) {
      loadEntityUsers();
    }
  }, [isAdminBooking, entityId, loadEntityUsers]);

  /**
   * Prefill contact number when user is selected
   */
  useEffect(() => {
    if (!isAdminBooking || !selectedUserId) {
      return;
    }

    const selectedUser = users.find((user) => user.id === parseInt(selectedUserId, 10));
    if (selectedUser && selectedUser.phone) {
      onContactPrefill(selectedUser.phone);
      setContactPrefilled(true);
    }
  }, [isAdminBooking, selectedUserId, users, onContactPrefill]);

  /**
   * Handle user selection change
   */
  const handleUserChange = useCallback((e) => {
    setSelectedUserId(e.target.value);
  }, []);

  /**
   * Handle bypass advance hours checkbox change
   */
  const handleBypassChange = useCallback((checked) => {
    setBypassAdvanceHours(checked);
  }, []);

  return {
    // State
    users,
    usersLoading,
    selectedUserId,
    bypassAdvanceHours,
    contactPrefilled,

    // Handlers
    handleUserChange,
    handleBypassChange,
    loadEntityUsers,
  };
};

export default useAdminUserSelection;
